import React, { useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { useApp } from '../../context/AppContext';
import { monthNames } from '../../utils/formatters';

// "Seu mês em 1 página" — um cartão-resumo bonito e COMPARTILHÁVEL do mês.
// O card usa cores fixas (tema escuro/dourado) de propósito: assim a imagem
// gerada fica idêntica e profissional em qualquer aparelho/tema, e evita que o
// html2canvas tropece em CSS variables. Formata dinheiro direto (sem passar por
// formatCurrency) pra que o Modo Privacidade não esconda os valores do relatório
// que o usuário escolheu gerar.

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const now = new Date();
const CURRENT_MONTH = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

function addMonth(monthKey, delta) {
  const [y, m] = monthKey.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthTitle(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  return `${monthNames[m - 1]} ${y}`;
}

export default function MonthReport() {
  const { state } = useApp();
  const cardRef = useRef(null);
  const [monthKey, setMonthKey] = useState(state.viewMonth || CURRENT_MONTH);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const data = useMemo(() => {
    const txs = (state.transactions || []).filter(t => (t.date || '').startsWith(monthKey) && !t.cardId);
    const paid = txs.filter(t => t.status === 'paid');
    const income = paid.filter(t => t.type === 'income').reduce((s, t) => s + (t.value || 0), 0);
    const expense = paid.filter(t => t.type === 'expense').reduce((s, t) => s + (t.value || 0), 0);
    const saved = income - expense;
    const savingsRate = income > 0 ? (saved / income) * 100 : 0;

    // Top categorias de gasto do mês
    const cats = state.categories || [];
    const byCat = {};
    paid.filter(t => t.type === 'expense').forEach(t => {
      byCat[t.categoryId] = (byCat[t.categoryId] || 0) + (t.value || 0);
    });
    const topCats = Object.entries(byCat)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([id, val]) => {
        const c = cats.find(x => x.id === id);
        return { name: c?.name || 'Outros', color: c?.color || '#888780', value: val };
      });
    const maxCat = topCats[0]?.value || 1;

    // Comparativo com o mês anterior (despesas pagas, sem cartão)
    const prevKey = addMonth(monthKey, -1);
    const prevExpense = (state.transactions || [])
      .filter(t => (t.date || '').startsWith(prevKey) && !t.cardId && t.status === 'paid' && t.type === 'expense')
      .reduce((s, t) => s + (t.value || 0), 0);
    const expDiffPct = prevExpense > 0 ? ((expense - prevExpense) / prevExpense) * 100 : null;

    return { income, expense, saved, savingsRate, topCats, maxCat, expDiffPct, count: paid.length };
  }, [state.transactions, state.categories, monthKey]);

  const userName = (state.user?.displayName || state.user?.name || '').split(' ')[0] || '';

  async function generateImage() {
    if (!cardRef.current) return null;
    const canvas = await html2canvas(cardRef.current, { backgroundColor: '#14161d', scale: 2, useCORS: true, logging: false });
    return await new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/png'));
  }

  async function handleShare() {
    setBusy(true); setMsg('');
    try {
      const blob = await generateImage();
      if (!blob) throw new Error('sem imagem');
      const file = new File([blob], `meu-mes-${monthKey}-maw.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Meu mês no MAW Finanças', text: `Meu resumo de ${monthTitle(monthKey)} 📊` });
        setMsg('');
      } else {
        downloadBlob(blob);
        setMsg('Imagem baixada! É só compartilhar onde quiser. 📤');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') setMsg('Não consegui gerar a imagem agora. Tente de novo.');
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload() {
    setBusy(true); setMsg('');
    try {
      const blob = await generateImage();
      if (blob) { downloadBlob(blob); setMsg('Imagem salva na galeria/downloads. 📥'); }
    } catch {
      setMsg('Não consegui gerar a imagem agora. Tente de novo.');
    } finally { setBusy(false); }
  }

  function downloadBlob(blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `meu-mes-${monthKey}-maw.png`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  const savedPositive = data.saved >= 0;

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div>
          <h1 className="page-title">Seu Mês em 1 Página</h1>
          <p className="page-subtitle">Um resumo bonito do seu mês — pronto pra compartilhar.</p>
        </div>
      </div>

      {/* Seletor de mês */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 16 }}>
        <button className="btn-ghost" onClick={() => setMonthKey(m => addMonth(m, -1))} aria-label="Mês anterior"
          style={{ width: 38, height: 38, borderRadius: 10 }}>‹</button>
        <div style={{ fontWeight: 700, fontSize: 15, minWidth: 150, textAlign: 'center', color: 'var(--text)' }}>{monthTitle(monthKey)}</div>
        <button className="btn-ghost" onClick={() => setMonthKey(m => addMonth(m, 1))} aria-label="Próximo mês"
          disabled={monthKey >= CURRENT_MONTH}
          style={{ width: 38, height: 38, borderRadius: 10, opacity: monthKey >= CURRENT_MONTH ? 0.4 : 1 }}>›</button>
      </div>

      {/* ── O CARTÃO COMPARTILHÁVEL ── (cores fixas de propósito) */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div ref={cardRef} style={{
          width: 400, maxWidth: '100%', boxSizing: 'border-box',
          background: 'linear-gradient(160deg, #191c26 0%, #12141b 100%)',
          border: '1px solid #2a2e3c', borderRadius: 20, padding: '26px 24px',
          color: '#f5f4ef', fontFamily: 'inherit',
        }}>
          {/* Marca + mês */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: '#c9a227', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#12141b', fontWeight: 900, fontSize: 15 }}>M</div>
              <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.3 }}>MAW <span style={{ color: '#c9a227' }}>Finanças</span></div>
            </div>
            <div style={{ fontSize: 11, color: '#9a9a93', textTransform: 'uppercase', letterSpacing: 1 }}>Fechamento</div>
          </div>

          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 2 }}>{monthTitle(monthKey)}</div>
          <div style={{ fontSize: 12, color: '#9a9a93', marginBottom: 20 }}>
            {userName ? `Resumo de ${userName}` : 'Seu resumo do mês'} · {data.count} lançamento{data.count !== 1 ? 's' : ''}
          </div>

          {/* Saldo do mês — destaque */}
          <div style={{ background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.25)', borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#c9a227', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>Saldo do mês</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: savedPositive ? '#3ddc84' : '#ff6b6b', marginTop: 2 }}>
              {savedPositive ? '' : '−'}{fmt(Math.abs(data.saved))}
            </div>
            <div style={{ fontSize: 11, color: '#9a9a93', marginTop: 2 }}>
              {data.income > 0 ? `Você guardou ${data.savingsRate.toFixed(0)}% do que entrou` : 'Sem entradas registradas no mês'}
            </div>
          </div>

          {/* Entradas x Saídas */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
            <div style={{ flex: 1, background: '#1e2230', borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ fontSize: 10.5, color: '#9a9a93', textTransform: 'uppercase', letterSpacing: 0.5 }}>Entradas</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#3ddc84', marginTop: 3 }}>{fmt(data.income)}</div>
            </div>
            <div style={{ flex: 1, background: '#1e2230', borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ fontSize: 10.5, color: '#9a9a93', textTransform: 'uppercase', letterSpacing: 0.5 }}>Saídas</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#ff6b6b', marginTop: 3 }}>{fmt(data.expense)}</div>
            </div>
          </div>

          {/* Comparativo com mês anterior */}
          {data.expDiffPct !== null && (
            <div style={{ fontSize: 12, marginBottom: 16, color: data.expDiffPct <= 0 ? '#3ddc84' : '#ff9f43', fontWeight: 600 }}>
              {data.expDiffPct <= 0
                ? `▼ Você gastou ${Math.abs(data.expDiffPct).toFixed(0)}% menos que no mês anterior`
                : `▲ Você gastou ${data.expDiffPct.toFixed(0)}% mais que no mês anterior`}
            </div>
          )}

          {/* Top categorias */}
          <div style={{ fontSize: 11, color: '#9a9a93', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, marginBottom: 10 }}>Onde seu dinheiro foi</div>
          {data.topCats.length === 0 && (
            <div style={{ fontSize: 12, color: '#9a9a93', padding: '6px 0 10px' }}>Nenhum gasto registrado neste mês.</div>
          )}
          {data.topCats.map((c, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: c.color, display: 'inline-block' }} />
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>{c.name}</span>
                </div>
                <span style={{ fontSize: 12.5, fontWeight: 700 }}>{fmt(c.value)}</span>
              </div>
              <div style={{ height: 6, background: '#262a38', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.max(4, (c.value / data.maxCat) * 100)}%`, background: c.color, borderRadius: 4 }} />
              </div>
            </div>
          ))}

          {/* Rodapé — a marca viral */}
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #2a2e3c', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 11, color: '#9a9a93' }}>Feito no <b style={{ color: '#c9a227' }}>MAW Finanças</b></div>
            <div style={{ fontSize: 11, color: '#9a9a93' }}>maw-financas.web.app</div>
          </div>
        </div>
      </div>

      {/* Ações — fora do card, não entram na imagem */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginTop: 20 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button className="btn-primary" onClick={handleShare} disabled={busy} style={{ minWidth: 170 }}>
            {busy ? 'Gerando…' : '📤 Compartilhar meu mês'}
          </button>
          <button className="btn-ghost" onClick={handleDownload} disabled={busy}>
            📥 Baixar imagem
          </button>
        </div>
        {msg && <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{msg}</div>}
        <div style={{ fontSize: 11.5, color: 'var(--muted)', textAlign: 'center', maxWidth: 360 }}>
          Dica: mande pro seu grupo da família ou pros amigos — quem vê, conhece o app. 😉
        </div>
      </div>
    </div>
  );
}
